/* eslint-disable @typescript-eslint/no-unused-vars */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { PERSONA_IMPORT_STATUS_IN_PROGRESS } from 'Utils/Constants';
import { SelectedPerson } from 'Types/GlobalSearchResultsTypes';
import {
  ConfirmPersonaRequest, DeleteJobTitleRequest, DeletePersonaRequest, DuplicateCheckRequest,
  GeneratedPersonaResult,
  GeneratePersonaRequest, JobTitleDeleted, JobTitlesReordered, JobTitleUpdated,
  GenerateImportPersonaRequest,
  ImportPersonaCompaniesRequest,
  ListPersonasRequest, Persona,
  PersonaDetailsRequest,
  PersonaFullDetails, PersonaImportStatusRequest, PersonaResultCount,
  PersonaResultCountRequest, PersonasLoaded,
  PersonaProspectsLoaded, PersonaResultsRequest,
  PersonaTitle, PersonasState, ReorderJobTitlesRequest,
  UpdateJobTitleRequest,
} from 'Types/PersonasTypes';

export const initialState: PersonasState = {
  personas: [],
  personaCount: 0,
  personasLoading: false,
  personasLoaded: false,
  personasPage: 0,
  personasEnded: false,
  searchText: '',
  deleteLoading: false,
  nameCheckLoading: false,
  nameCheckError: '',
  draftPersonaName: '',
  personaNameChecked: false,
  draftPersonaId: '',
  generateLoading: false,
  generatedTitles: [],
  personaGenerated: false,
  confirmLoading: false,
  confirmError: '',
  personaConfirmed: false,
  detailsLoading: false,
  personaDetails: null,
  deletingTitleIds: [],
  togglingTitleIds: [],
  resultCountLoading: false,
  resultCountLoaded: false,
  resultCount: 0,
  listStatus: '',
  listDescription: '',
  resultsTotal: 0,
  resultsProspects: [],
  resultsPage: 0,
  resultsLoading: false,
  resultsEnded: false,
  resultsDrawerOpen: false,
  prospectGroupOpen: false,
  prospectGroupShowNew: false,
  selectedProspects: [],
  importLoading: false,
  importId: 0,
  importFileName: '',
  importGenerateLoading: false,
  importGenerateStarted: false,
  importPersonaStatus: '',
  pendingImportId: 0,
};

const applyTitleChange = (
  state: PersonasState,
  personaId: string,
  jobTitleId: string,
  change: Partial<PersonaTitle>,
) => {
  const update = (titles: PersonaTitle[]) => {
    const title = titles.find((entry) => entry.id === jobTitleId);
    if (title) Object.assign(title, change);
  };
  if (state.personaDetails?.id === personaId) update(state.personaDetails.titles);
  if (state.draftPersonaId === personaId) update(state.generatedTitles);
};

const PersonasSlice = createSlice({
  name: 'personas',
  initialState,
  reducers: {
    getPersonas(state, action: PayloadAction<ListPersonasRequest>) {
      const { page } = action.payload ?? {};
      state.personasLoading = true;
      state.searchText = action.payload?.searchText?.trim() ?? '';
      if (page && page > 1) return;
      state.personasPage = initialState.personasPage;
      state.personasEnded = initialState.personasEnded;
    },
    setPersonas(state, action: PayloadAction<PersonasLoaded>) {
      const {
        personaCount, personas, page, ended,
      } = action.payload;
      state.personas = page > 1 ? [...state.personas, ...personas] : personas ?? [];
      state.personaCount = personaCount ?? 0;
      state.personasPage = page;
      state.personasEnded = ended;
      state.personasLoading = false;
      state.personasLoaded = true;
    },
    getPersonasFailed(state, action: PayloadAction<number>) {
      state.personasLoading = false;
      state.personasLoaded = true;
      state.personasEnded = action.payload <= 1;
    },
    deletePersona(state, action: PayloadAction<DeletePersonaRequest>) {
      state.deleteLoading = true;
    },
    personaDeleted(state, action: PayloadAction<string>) {
      state.personas = state.personas.filter((persona) => persona.id !== action.payload);
      state.personaCount = Math.max(state.personaCount - 1, 0);
      state.deleteLoading = false;
    },
    deletePersonaFailed(state) {
      state.deleteLoading = false;
    },
    getPersonaDetails(state, action: PayloadAction<PersonaDetailsRequest>) {
      if (state.personaDetails?.id === action.payload.personaId) return;
      state.detailsLoading = true;
      state.personaDetails = null;
      state.resultCount = initialState.resultCount;
      state.listStatus = initialState.listStatus;
      state.listDescription = initialState.listDescription;
      state.resultCountLoaded = false;
    },
    setPersonaDetails(state, action: PayloadAction<PersonaFullDetails>) {
      state.detailsLoading = false;
      state.personaDetails = action.payload;
    },
    getPersonaDetailsFailed(state) {
      state.detailsLoading = false;
      state.resultCountLoaded = true;
    },
    getPersonaResultCount(state, action: PayloadAction<PersonaResultCountRequest>) {
      state.resultCountLoading = true;
    },
    setPersonaResultCount(state, action: PayloadAction<PersonaResultCount>) {
      const { payload } = action;
      state.resultCountLoading = false;
      state.resultCountLoaded = true;
      state.resultCount = payload.totalCount;
      state.listStatus = payload.listStatus;
      state.listDescription = payload.listDescription;
    },
    getPersonaResultCountFailed(state) {
      state.resultCountLoading = false;
      state.resultCountLoaded = true;
    },
    setDetailTitles(state, action: PayloadAction<PersonaTitle[]>) {
      if (state.personaDetails) {
        state.personaDetails.titles = action.payload;
      }
    },
    deleteJobTitle(state, action: PayloadAction<DeleteJobTitleRequest>) {
      const { jobTitleId } = action.payload;
      if (!state.deletingTitleIds.includes(jobTitleId)) {
        state.deletingTitleIds.push(jobTitleId);
      }
    },
    jobTitleDeleted(state, action: PayloadAction<JobTitleDeleted>) {
      const { personaId, jobTitleId } = action.payload;
      state.deletingTitleIds = state.deletingTitleIds.filter((id) => id !== jobTitleId);
      const remove = (previous: PersonaTitle[]) => previous.filter(
        (entry) => entry.id !== jobTitleId,
      );
      if (state.personaDetails?.id === personaId) {
        state.personaDetails.titles = remove(state.personaDetails.titles);
      }
      if (state.personaGenerated && state.draftPersonaId === personaId) {
        state.generatedTitles = remove(state.generatedTitles);
      }
    },
    deleteJobTitleFailed(state, action: PayloadAction<string>) {
      state.deletingTitleIds = state.deletingTitleIds.filter((id) => id !== action.payload);
    },
    updateJobTitle(state, action: PayloadAction<UpdateJobTitleRequest>) {
      const { personaId, jobTitleId, isActive } = action.payload;
      if (!state.togglingTitleIds.includes(jobTitleId)) {
        state.togglingTitleIds.push(jobTitleId);
      }
      if (isActive !== undefined) {
        applyTitleChange(state, personaId, jobTitleId, { enabled: isActive });
      }
    },
    jobTitleUpdated(state, action: PayloadAction<JobTitleUpdated>) {
      const {
        personaId, jobTitleId, name, enabled,
      } = action.payload;
      state.togglingTitleIds = state.togglingTitleIds.filter((id) => id !== jobTitleId);
      applyTitleChange(state, personaId, jobTitleId, {
        ...enabled === undefined ? {} : { enabled },
        ...name ? { name } : {},
      });
    },
    updateJobTitleFailed(state, action: PayloadAction<JobTitleUpdated>) {
      const { personaId, jobTitleId, enabled } = action.payload;
      state.togglingTitleIds = state.togglingTitleIds.filter((id) => id !== jobTitleId);
      if (enabled !== undefined) {
        applyTitleChange(state, personaId, jobTitleId, { enabled });
      }
    },
    reorderJobTitles(state, action: PayloadAction<ReorderJobTitlesRequest>) { },
    jobTitlesReordered(state, action: PayloadAction<JobTitlesReordered>) {
      const { personaId, titles } = action.payload;
      if (!titles.length || state.personaDetails?.id !== personaId) return;
      const previous = state.personaDetails.titles;
      state.personaDetails.titles = titles.map((title) => {
        const existing = previous.find(
          (entry) => entry.id === title.id || entry.name === title.name,
        );
        return existing ? { ...title, enabled: existing.enabled } : title;
      });
    },
    setDraftPersonaId(state, action: PayloadAction<string>) {
      state.draftPersonaId = action.payload;
    },
    setDraftPersonaName(state, action: PayloadAction<string>) {
      state.draftPersonaName = action.payload;
    },
    checkPersonaName(state, action: PayloadAction<DuplicateCheckRequest>) {
      state.nameCheckLoading = true;
      state.nameCheckError = '';
      state.personaNameChecked = false;
    },
    personaNameAvailable(state, action: PayloadAction<string>) {
      state.nameCheckLoading = false;
      state.draftPersonaName = action.payload;
      state.personaNameChecked = true;
    },
    checkPersonaNameFailed(state, action: PayloadAction<string>) {
      state.nameCheckLoading = false;
      state.nameCheckError = action.payload;
    },
    clearNameCheckError(state) {
      state.nameCheckError = '';
    },
    generatePersona(state, action: PayloadAction<GeneratePersonaRequest>) {
      state.generateLoading = true;
      state.generatedTitles = [];
      state.personaGenerated = false;
    },
    setGeneratedPersona(state, action: PayloadAction<GeneratedPersonaResult>) {
      const { payload } = action;
      state.draftPersonaId = payload.personaId;
      state.draftPersonaName = payload.personaName || state.draftPersonaName;
      state.generatedTitles = payload.titles;
      state.generateLoading = false;
      state.personaGenerated = true;
    },
    setGeneratedTitles(state, action: PayloadAction<PersonaTitle[]>) {
      state.generatedTitles = action.payload;
      state.generateLoading = false;
      state.personaGenerated = true;
    },
    generatePersonaFailed(state) {
      state.generateLoading = false;
    },
    confirmPersona(state, action: PayloadAction<ConfirmPersonaRequest>) {
      state.confirmLoading = true;
      state.confirmError = '';
      state.personaConfirmed = false;
    },
    setConfirmedPersona(state, action: PayloadAction<Persona>) {
      const index = state.personas.findIndex((p) => p.id === action.payload.id);
      const existing = index >= 0 ? state.personas[index] : undefined;
      const persona = {
        ...action.payload,
        name: action.payload.name || existing?.name || state.draftPersonaName,
        previewPersons: action.payload.previewPersons.length
          ? action.payload.previewPersons
          : existing?.previewPersons ?? [],
      };
      if (index >= 0) {
        state.personas[index] = persona;
      } else {
        state.personas.push(persona);
        state.personaCount += 1;
      }
      state.confirmLoading = false;
      state.personaConfirmed = true;
    },
    confirmPersonaFailed(state, action: PayloadAction<string>) {
      state.confirmLoading = false;
      state.confirmError = action.payload;
    },
    getPersonaProspects(state, action: PayloadAction<PersonaResultsRequest>) {
      const { personaId, page } = action.payload;
      state.resultsLoading = true;
      if (page && page > 1) return;
      state.resultsTotal = initialState.resultsTotal;
      state.resultsProspects = [];
      state.resultsPage = initialState.resultsPage;
      state.resultsEnded = initialState.resultsEnded;
      state.selectedProspects = initialState.selectedProspects;
    },
    setPersonaProspects(state, action: PayloadAction<PersonaProspectsLoaded>) {
      const {
        resultCount, page, prospects, ended,
      } = action.payload;
      state.resultsLoading = false;
      state.resultsTotal = resultCount;
      state.resultsProspects = page > 1 ? [...state.resultsProspects, ...prospects] : prospects;
      state.resultsPage = page;
      state.resultsEnded = ended;
    },
    getPersonaProspectsFailed(state, action: PayloadAction<number>) {
      state.resultsLoading = false;
      state.resultsEnded = action.payload <= 1;
    },
    prospectFollowHandling(state, action: PayloadAction<number | undefined>) {
      if (action.payload === undefined) return;
      const id = String(action.payload);
      const index = state.resultsProspects.findIndex((entry) => entry.id === id);
      if (index >= 0) state.resultsProspects[index].personAdded = true;
    },
    prospectUnfollowHandling(state, action: PayloadAction<number[]>) {
      const ids = action.payload.map((entry) => String(entry));
      state.resultsProspects.forEach((prospect, index) => {
        if (ids.includes(prospect.id)) {
          state.resultsProspects[index].personAdded = false;
        }
      });
    },
    setResultsDrawerOpen(state, action: PayloadAction<boolean>) {
      state.resultsDrawerOpen = action.payload;
      if (!action.payload) {
        state.prospectGroupOpen = initialState.prospectGroupOpen;
        state.prospectGroupShowNew = initialState.prospectGroupShowNew;
      }
    },
    openProspectGroupFlow(state) {
      state.prospectGroupOpen = true;
      state.prospectGroupShowNew = true;
    },
    setProspectGroupOpen(state, action: PayloadAction<boolean>) {
      state.prospectGroupOpen = action.payload;
      if (!action.payload) state.prospectGroupShowNew = initialState.prospectGroupShowNew;
    },
    setProspectGroupShowNew(state, action: PayloadAction<boolean>) {
      state.prospectGroupShowNew = action.payload;
    },
    clearPersonaProspects(state) {
      state.resultsTotal = initialState.resultsTotal;
      state.resultsProspects = [];
      state.resultsPage = initialState.resultsPage;
      state.resultsLoading = initialState.resultsLoading;
      state.resultsEnded = initialState.resultsEnded;
      state.prospectGroupOpen = initialState.prospectGroupOpen;
      state.prospectGroupShowNew = initialState.prospectGroupShowNew;
      state.selectedProspects = initialState.selectedProspects;
    },
    toggleSelectedProspect(state, action: PayloadAction<SelectedPerson>) {
      const { personId } = action.payload;
      const index = state.selectedProspects.findIndex((entry) => entry.personId === personId);
      if (index === -1) {
        state.selectedProspects.push(action.payload);
      } else {
        state.selectedProspects.splice(index, 1);
      }
    },
    clearSelectedProspects(state) {
      state.selectedProspects = [];
    },
    resetGeneratedPersona(state) {
      state.nameCheckLoading = initialState.nameCheckLoading;
      state.nameCheckError = initialState.nameCheckError;
      state.draftPersonaName = initialState.draftPersonaName;
      state.personaNameChecked = initialState.personaNameChecked;
      state.draftPersonaId = initialState.draftPersonaId;
      state.generateLoading = initialState.generateLoading;
      state.generatedTitles = initialState.generatedTitles;
      state.personaGenerated = initialState.personaGenerated;
      state.confirmLoading = initialState.confirmLoading;
      state.confirmError = initialState.confirmError;
      state.personaConfirmed = initialState.personaConfirmed;
      state.importLoading = initialState.importLoading;
      state.importId = initialState.importId;
      state.importFileName = initialState.importFileName;
      state.importGenerateLoading = initialState.importGenerateLoading;
      state.importGenerateStarted = initialState.importGenerateStarted;
      state.importPersonaStatus = initialState.importPersonaStatus;
    },
    importPersonaCompanies(state, action: PayloadAction<ImportPersonaCompaniesRequest>) {
      state.importLoading = true;
    },
    setImportedPersona(state, action: PayloadAction<{ importId: number, fileName: string }>) {
      state.importId = action.payload.importId;
      state.importFileName = action.payload.fileName;
      state.importLoading = false;
      state.personaNameChecked = initialState.personaNameChecked;
      state.draftPersonaId = initialState.draftPersonaId;
      state.draftPersonaName = initialState.draftPersonaName;
      state.nameCheckError = initialState.nameCheckError;
    },
    importPersonaCompaniesFailed(state) {
      state.importLoading = false;
    },
    generateImportPersona(state, action: PayloadAction<GenerateImportPersonaRequest>) {
      state.importGenerateLoading = true;
      state.importGenerateStarted = false;
      state.importPersonaStatus = initialState.importPersonaStatus;
    },
    importPersonaGenerationStarted(state, action: PayloadAction<{ importId: number }>) {
      state.importGenerateLoading = false;
      state.importGenerateStarted = true;
      state.pendingImportId = action.payload.importId;
    },
    resumeImportPersonaStatus(state, action: PayloadAction<{ importId: number }>) {
      state.pendingImportId = action.payload.importId;
    },
    generateImportPersonaFailed(state) {
      state.importGenerateLoading = false;
    },
    setImportPersonaStatus(state, action: PayloadAction<string>) {
      state.importPersonaStatus = action.payload;
      if (action.payload !== PERSONA_IMPORT_STATUS_IN_PROGRESS) {
        state.pendingImportId = initialState.pendingImportId;
        state.importGenerateStarted = initialState.importGenerateStarted;
      }
    },
    pollImportPersonaStatus(state, action: PayloadAction<PersonaImportStatusRequest>) { },
    returnToGilroyStart(state) {
      state.personaNameChecked = initialState.personaNameChecked;
      state.generateLoading = initialState.generateLoading;
      state.generatedTitles = initialState.generatedTitles;
      state.personaGenerated = initialState.personaGenerated;
      state.confirmLoading = initialState.confirmLoading;
      state.confirmError = initialState.confirmError;
      state.personaConfirmed = initialState.personaConfirmed;
      state.importGenerateLoading = initialState.importGenerateLoading;
      state.importGenerateStarted = initialState.importGenerateStarted;
      state.importPersonaStatus = initialState.importPersonaStatus;
    },
  },
});

export const { actions, reducer, name: sliceKey } = PersonasSlice;
